import {Component, EventEmitter, HostListener, inject, Input, OnInit, Output} from '@angular/core';
import {ModalsService} from '../../../../services/modals.service';
import {
  ButtonRecordValue,
  HistoryRecordValue,
  RecordData,
  RecordTableColumnParams
} from '../../../../types/records-table.types';
import {buttonKeyConfig, iconConfig} from "./configs/modal-record-table-item-config";
import {ResourceParams} from "../../../../models/resource/resources";
import {defaultResourceParams} from "../../../../models/resource/default-resource-params";
import {UrlQueryParamsStateService} from "../../../../services/table-services/url-query-params-state.service";
import {ModalItemPasteStateService} from "./services/modal-item-paste-state.service";
import {NOTES_FILE_TYPES} from "../../../../constants/files.const";
import {ModalRecordTableItemService} from "./services/modal-record-table-item.service";
import {ModalRecordTableItemValidService} from "./services/modal-record-table-item-valid.service";
import {OpenFilesViewerModalParams} from "../../../../types/file.types";
import {defaultRecordData} from "../../../../models/default-record-data";
import { SnackbarService } from "../../../../services/snackbar.service";

@Component({
  selector: 'app-modal-record-table-item',
  standalone: false,
  templateUrl: './modal-record-table-item.component.html',
  styleUrl: './modal-record-table-item.component.scss'
})
export class ModalRecordTableItemComponent implements OnInit {
  @Input({ required: true }) columnParam!: RecordTableColumnParams;
  @Input({ required: true }) recordData: RecordData = defaultRecordData;
  @Input({ required: true }) resourceParams: ResourceParams = defaultResourceParams;
  @Input({ required: true }) isDataExists: boolean = false;
  @Input() iconClassName: string = '';
  @Output() reloadTableData: EventEmitter<void> = new EventEmitter<void>;

  @HostListener('document:paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    this.modalItemPasteStateService.isModalItemPasteStateByRecordId(Number(this.recordData.id)) && this.handlePaste(event);
  }

  //public telegramNotificationsStateService: TelegramNotificationsStateService = inject(TelegramNotificationsStateService);
  private modalsService: ModalsService = inject(ModalsService);
  private modalRecordTableItemService: ModalRecordTableItemService = inject(ModalRecordTableItemService);
  private modalRecordTableItemValidService: ModalRecordTableItemValidService = inject(ModalRecordTableItemValidService);
  //private telegramChatModalService: TelegramChatModalService = inject(TelegramChatModalService);
  private urlQueryParamsStateService: UrlQueryParamsStateService = inject(UrlQueryParamsStateService);
  private modalItemPasteStateService: ModalItemPasteStateService = inject(ModalItemPasteStateService);
  private snackbarService: SnackbarService = inject(SnackbarService);

  public buttonKeys: Array<string> = [];
  private fileTypeName: string = '';

  public ngOnInit(): void {
    this.initializeButtonKeys();
    this.openModalsForUrlQueryParams();
  }

  public get iconName(): string {
    const icon = iconConfig[this.columnParam.key];
    return icon !== undefined ? icon : 'info_outline';
  }

  public getClassIcon(key: string): string {
    return this.modalRecordTableItemService.classIcon({
      key: key,
      recordData: this.recordData
    });
  }

  /*TODO: раскомментировать если включат telegram
  public getBadge(recordId: number | string): number {
    return this.telegramNotificationsStateService.getTelegramNotificationsById(Number(recordId));
  }*/

  public async handleModalButtonClick(key?: string): Promise<void> {
    switch (this.columnParam.key) {
      case 'audienceId': {
        await this.modalsService.getStatusFromAudienceModal(this.columnParam.key, this.recordData);
        this.reloadTableData.emit();
        break;
      }
      case 'bankDetails': {
        if (!this.modalRecordTableItemValidService.isValidBankDetails(this.recordData)) { return; }

        await this.modalsService.openBankDetails(this.columnParam.key, this.recordData);
        this.reloadTableData.emit();
        break;
      }
      case 'bloggerContact': {
        if (!this.modalRecordTableItemValidService.isValidBloggerContact(this.recordData)) { return; }

        await this.modalsService.getStatusFromContactModal(this.columnParam.key, this.recordData);
        this.reloadTableData.emit();
        break;
      }
      case 'blogger': {
        await this.modalsService.getStatusFromBloggerCardModal({
          columnParamKey: this.columnParam.key,
          columnParam: this.columnParam,
          recordData: this.recordData,
          path: this.resourceParams.path
        });

        this.reloadTableData.emit();
        break;
      }
      case 'repeatedRecordListId': {
        if (!this.recordData['repeatedRecordListId']) { return; }

        await this.modalsService.openRepeatsModal(this.recordData);
        break;
      }
      case 'advertisementInitialStatistics':
      case 'initialStatistics':
      case 'statistics':
        await this.modalsService.openAdvertisementStatisticsModal(this.columnParam.key, this.recordData);
        this.reloadTableData.emit();
        break;
      case 'documents': {
        if (!this.modalRecordTableItemValidService.isValidDocumentModal(this.recordData)) { return; }

        await this.modalsService.openDocumentsModal(this.recordData, this.resourceParams);
        this.reloadTableData.emit();
        break;
      }
      case 'history': {
        const historyData = (this.recordData['history'] as HistoryRecordValue).history;
        if (!historyData?.length) { return; }

        await this.modalsService.openHistoryChangesModal({
          historyChangesData: historyData,
          columnKey: this.columnParam.key
        });
        break;
      }
      case 'advertisementPackage':
        await this.modalsService.openAdvertisementPackageModal(this.columnParam.key, this.recordData);
        break;
      case 'telegram':
        /*TODO: раскомментировать если включат telegram
        const userChatQueryParams = await this.telegramChatModalService.getUserChatQueryParams(Number(this.recordData.id));
        if (!userChatQueryParams) { return; }

        await this.modalsService.openTelegramChatModal(Number(this.recordData.id));*/
        this.snackbarService.infoShowMessage('Извините, этот функционал временно отключен.');
        break;
      case 'hypothesisModal':
        await this.modalsService.openHypothesisModal(this.recordData);
        break;
      case 'customer':
        await this.modalsService.openCustomerModal(this.recordData);
        this.reloadTableData.emit();
        break;
      case 'media':
        await this.modalsService.openFeedbackMediaModal(this.recordData);
        break;
      case 'chatHistory':
        /*TODO: раскомментировать если включат telegram
        await this.modalsService.openTelegramChatHistory(Number(this.recordData.id));*/
        this.snackbarService.infoShowMessage('Извините, этот функционал временно отключен.');
        break;
      case 'productCard':
        const isValid = this.modalRecordTableItemValidService.isValidProductCardModal({
          path: this.resourceParams.path,
          recordData: this.recordData,
        });

        if (isValid) { return; }

        const productCardId = (this.recordData['productCard'] as ButtonRecordValue).id || 0;
        if (productCardId) {
          this.modalRecordTableItemService.openProductCardPage(Number(productCardId));
          return;
        }

        await this.modalsService.openProductCardModal(this.resourceParams.path, this.recordData);
        this.reloadTableData.emit();
        break;
      case 'defects':
        if (!this.modalRecordTableItemValidService.isValidDefectsModal(this.recordData)) { return; }

        await this.modalsService.openDefectsModal(this.recordData);
        this.reloadTableData.emit();
        break;
      case 'noteFiles':
        this.fileTypeName = NOTES_FILE_TYPES;
        if (key === 'noteFiles') {
          await this.modalsService.openNoteFilesModal(this.recordData);
          this.reloadTableData.emit();
        } else {
          await this.modalRecordTableItemService.handleSaveNoteFile(this.recordData);
        }
        break;
      case 'productPackages':
      case 'declarations':
      case 'certificates':
      case 'factoryBankDetails':
      case 'turnkeyCostToWarehouses':
      case 'turnkeyCostToMarketplaces':
      case 'technicalConclusions':
      case 'productCompositions':
      case 'nmIds':
      case 'skus':
      case 'unitEconomicsCalculations':
        await this.modalsService.openTechnicalParameterModal(this.recordData, this.columnParam.key);
        this.reloadTableData.emit();
        break;
      case 'noteChangePrice':
      case 'advertisementKey':
        if (!this.modalRecordTableItemValidService.isValidNoteModal(this.recordData, this.columnParam.key)) { return; }

        await this.modalsService.openNoteModal(this.recordData, this.columnParam.key, this.resourceParams.path);
        this.reloadTableData.emit();
        break;
      case 'listings':
      case 'richContent':
      case 'aiVideos':
      case 'baseVideos':
      case 'packageDesignDetails':
      case 'rawPhotos':
      case 'rawVideos':
      case 'otherContent':
      case 'banners':
      case 'socialMedia':
      case 'mainPhoto':
        await this.modalsService.openDesignBlocksModal(this.recordData, this.columnParam.key);
        this.reloadTableData.emit();
        break;
      case 'checkYandex':
        await this.modalsService.openYandexDataCheckModal(this.recordData, this.resourceParams.path);
        this.reloadTableData.emit();
        break;
      case 'usp':
      case 'productValues':
      case 'productDescriptions':
      case 'productUsageRules':
      case 'websiteLinks':
      case 'websiteBackups':
      case 'marketingDrafts':
        await this.modalsService.openMarketingBlocksModal(this.recordData, this.columnParam.key);
        this.reloadTableData.emit();
        break;
      case 'changeLimitDto':
        await this.modalsService.openChangeRrcLimitModal(this.recordData, this.columnParam.key);
        this.reloadTableData.emit();
        break;
      case 'initialVideo':
      case 'finalVideo':
        const openFilesViewerModalParams: OpenFilesViewerModalParams = this.modalRecordTableItemService.setOpenFilesViewerModalParams(this.columnParam.key, this.recordData);
        await this.modalsService.openSingleTypeFilesViewerModal(openFilesViewerModalParams);
        break;
      case 'videoFeedback':
        const textDialogData = this.modalRecordTableItemService.setTextDialogData(this.recordData, this.columnParam.key);
        await this.modalsService.openEditorTextModal(textDialogData);
        this.reloadTableData.emit();
        break;
      case 'noPackageModalDto':
      case 'singleItemPackageModalDto':
      case 'transportBoxModalDto':
      case 'palletModalDto':
      case 'truck33PalletsModalDto':
      case 'gazelle6PalletsModalDto':
      case 'gazelle10PalletsModalDto':
        await this.modalsService.openProductPackagesModal(this.columnParam.key, this.recordData);
        this.reloadTableData.emit();
        break;
      case 'competitorArticles':
        await this.modalsService.openCompetitorBrandsModal(this.columnParam.key, this.recordData);
        this.reloadTableData.emit();
        break;
      case 'dates':
        await this.modalsService.openPriceLevelsModalConfigModal(this.columnParam.key, this.recordData);
        break;
      case 'testingResultFiles':
        await this.modalsService.openTestingResultFilesModal(this.columnParam.key, this.recordData);
        this.reloadTableData.emit();
        break;
      case 'productCardQualityControlApprovals':
        await this.modalsService.openCreateQualityControlModal(this.columnParam.key, String(this.recordData.id));
        this.reloadTableData.emit();
        break;
      case 'productCardGroupModalDto':
        if (!this.modalRecordTableItemValidService.isValidWildberriesProductCardsModal(this.recordData, this.columnParam.key)) { return; }

        await this.modalsService.openWildberriesProductCardsModal(this.columnParam.key, this.recordData);
        this.reloadTableData.emit();
        break;
    }
  }

  public getTooltip(key: string): string {
    return (this.recordData[key] as ButtonRecordValue)?.text || '';
  }

  private initializeButtonKeys(): void {
    if (!buttonKeyConfig.hasOwnProperty(this.columnParam.key)) { return; }

    this.buttonKeys = buttonKeyConfig[this.columnParam.key];
  }

  private openModalsForUrlQueryParams(): void {
    const hasValue = this.urlQueryParamsStateService.hasUrlQueryParamsState();
    if (hasValue) {
      for (const [ key, value ] of Object.entries(this.urlQueryParamsStateService.urlQueryParamsState())) {
        const isOpen = this.recordData.id === Number(value);
        isOpen && this.handleModalButtonClick(key);
      }
    }
  }

  private async handlePaste(event: ClipboardEvent): Promise<void> {
    const files = this.modalRecordTableItemService.getFilePaste(event);
    if (!files.length) { return; }

    event.preventDefault();
    await this.modalsService.openItemPasteProgressModalComponent({
      id: Number(this.recordData.id),
      files: files,
      type: this.fileTypeName,
      entityFileType: 'NOTE'
    });

    this.reloadTableData.emit();
  }
}
